import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { IconPicker } from '@/components/dueDiligence/IconPicker/IconPicker';
import { DueDiligenceScope } from 'shared/types/due-diligence';
import { CategoryFormData, SCOPE_OPTIONS } from './types';

interface CategoryBasicsTabProps {
  formData: CategoryFormData;
  setFormData: React.Dispatch<React.SetStateAction<CategoryFormData>>;
  isEditMode: boolean;
}

export const CategoryBasicsTab: React.FC<CategoryBasicsTabProps> = ({
  formData,
  setFormData,
  isEditMode,
}) => {
  const handleInputChange = (field: keyof CategoryFormData, value: string | boolean | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Category Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Categorienaam <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Bijv. Vergunningen"
                required
              />
              <p className="text-xs text-muted-foreground">
                De naam van de due diligence categorie
              </p>
            </div>

            {/* Scope */}
            <div className="space-y-2">
              <Label htmlFor="scope">Bereik</Label>
              <SimpleDropdown
                value={formData.scope}
                onChange={(value) => handleInputChange('scope', value as DueDiligenceScope)}
                options={SCOPE_OPTIONS}
                placeholder="Selecteer bereik"
              />
              <p className="text-xs text-muted-foreground">
                Op welk niveau geldt deze categorie
              </p>
            </div>
          </div>

          {/* Icon Picker */}
          <div className="mb-4">
            <IconPicker
              value={formData.icon}
              onChange={(value) => handleInputChange('icon', value)}
              defaultCategory="real_estate"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Optionele beschrijving van de categorie..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Extra informatie over deze categorie (optioneel)
            </p>
          </div>

          {/* Status Toggle - Only in Edit Mode */}
          {isEditMode && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="active">Status</Label>
                  <p className="text-sm text-muted-foreground">
                    Inactieve categorieën worden verborgen in de interface
                  </p>
                </div>
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => handleInputChange('active', checked)}
                />
              </div>
              <div className="mt-2">
                <p className="text-xs text-muted-foreground">
                  {formData.active
                    ? 'Deze categorie is actief en zichtbaar voor gebruikers'
                    : 'Deze categorie is inactief en verborgen voor gebruikers'}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
